import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import { Box, CircularProgress, Divider, IconButton, InputAdornment, Pagination, TextField } from "@mui/material";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import DialogLayout from "../../../components/layout/DialogLayout";
import { formatDateTime, toLocalePriceNoDecimal } from "../../../utils/StringUtil";
import { apiGetInvestorWisePayouts } from "../../../utils/api/api";
import { RECORD_DISPLAY_LIMIT } from "../../../utils/constants/constants";

const ViewPayoutSchedule = ({ open, toggleDialog, scheme }) => {

    const [searchTerm, setSearchTerm] = useState("");
    const [searchDate, setSearchDate] = useState("");
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [payoutList, setPayoutList] = useState([]);

    const debouncedSearch = useCallback(
        _.debounce((searchValue) => {
            setSearchTerm(searchValue);
        }, 100),
        []
    );

    const handleSearch = (value) => {
        debouncedSearch(value);
    }

    const handlePageChange = (_event, value) => {
        setPage(value);
    };

    const getInvestorWisePayouts = (param = {}) => {
        param.oppRid = scheme.oppRid;
        param.name = searchTerm;
        param.page = page;
        param.limit = RECORD_DISPLAY_LIMIT;
        param.date = searchDate;

        setIsLoading(true);

        apiGetInvestorWisePayouts(param)
            .then((data) => {
                setIsLoading(false);
                setPayoutList(data.payoutList);
                setTotalRecords(data.records);
            }).catch((error) => {
                setIsLoading(false);
                console.log(error);
            });
    }

    useEffect(() => {
        if (open) {
            getInvestorWisePayouts();
        }

        if (!open) {
            setPage(1);
            setSearchTerm("");
            setPayoutList([]);
            setTotalRecords(0);
            setSearchDate("");
        }
    }, [open, page, searchTerm, searchDate]);

    const styles = {
        stickyColumn: {
            position: "sticky",
            left: 0,
            zIndex: 1,
        },
    };

    return (
        <DialogLayout
            open={open}
            title="View Payout Schedule"
            closeAction={toggleDialog}
            maxWidth={"lg"}
        >
            <div
                className="d-flex justify-content-between align-items-center bg-white  mx-4 my-2"
            // style={{ borderRadius: "5px" }}
            >
                <div className="d-flex gap-3">
                    <div
                        className="search-input-btn d-flex justify-content-between rounded bg-white px-3"
                        style={{
                            border: "1px solid #000",
                            minWidth: "50px",
                            height: "100%"
                        }}
                    >
                        <div className="my-auto">
                            <SearchIcon sx={{ cursor: "pointer" }} />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="form-control custom-form-control border-0 bg-transparent fs-large-regular"
                            placeholder="Search"
                        />
                        <div onClick={() => setSearchTerm("")} className="my-auto">
                            <CloseIcon sx={{ cursor: "pointer" }} />
                        </div>
                    </div>
                    <TextField
                        id="outlined-basic"
                        type="date"
                        name="searchDate"
                        label="Payout Date"
                        variant="outlined"
                        value={searchDate}
                        onChange={(e) => setSearchDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ minWidth: 150 }}
                        InputProps={{
                            endAdornment: searchDate ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setSearchDate("")}
                                        edge="end"
                                        size="small"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>

                <div className="ms-auto d-flex align-items-center gap-3">
                    <Box>
                        <p className="section-label mb-1" style={{ fontSize: "14px" }}>
                            Deployed Capital
                        </p>
                        <p className="fs-med-medium p-0 m-0">
                            ₹ {toLocalePriceNoDecimal(scheme?.fundReceived)}
                        </p>
                    </Box>
                    {totalRecords > RECORD_DISPLAY_LIMIT && (
                        <Pagination
                            color="secondary"
                            size="small"
                            siblingCount={0}
                            onChange={handlePageChange}
                            count={Math.max(1, totalRecords > 0 ? Math.ceil(totalRecords / RECORD_DISPLAY_LIMIT) : 1)}
                            page={page}
                        />

                    )}
                </div>
            </div>
            <Divider />
            {
                isLoading ? (
                    <CircularProgress />
                ) : payoutList.length > 0 ? (
                    <div className="d-flex">
                        <table className="table table-borderless w-100 m-4">
                            <thead className="sticky-table-header">
                                <tr className="fs-normal-semibold text-center">
                                    <th scope="col" className="sticky-column" >
                                        <p className="mb-2">Investor Name</p>
                                    </th>
                                    <th scope="col"><p className="mb-2 text-center">Payout Date</p></th>
                                    <th scope="col">
                                        <p className="mb-2 text-end">Gross Amount</p>
                                    </th>
                                    <th scope="col">
                                        <p className="mb-2 text-end">Fee</p>
                                    </th>
                                    <th scope="col">
                                        <p className="mb-2 text-end">TDS</p>
                                    </th>
                                    <th scope="col">
                                        <p className="mb-2 text-end">Net Amount</p>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {payoutList.map((payout, payoutIndex) => {

                                    const stripeBackground = payoutIndex % 2 === 0 ? '#F2F2F2' : '#ffffff';

                                    return (
                                        <React.Fragment key={payout.payoutRid}>
                                            {payout.payoutDetailList.map((detail, idx) => {
                                                return (
                                                    <tr
                                                        key={`${payout.payoutRid}-${idx}`}
                                                        className="fs-normal-medium text-center"
                                                        style={{
                                                            border: '1.5px solid #e0e0e0',

                                                        }}
                                                    >
                                                        {idx === 0 && (
                                                            <td
                                                                className=" pt-3 "
                                                                style={{
                                                                    ...styles.stickyColumn,
                                                                    backgroundColor: stripeBackground,
                                                                    verticalAlign: 'middle',
                                                                    border: '1.5px solid #e0e0e0'
                                                                }}
                                                                rowSpan={payout.payoutDetailList.length + 1}
                                                            >
                                                                <span >
                                                                    {payout?.invName ?? ''}
                                                                    {payout?.invCode && ` (${payout.invCode})`}
                                                                </span>
                                                            </td>
                                                        )}
                                                        <td className="pt-3 text-center" style={{ backgroundColor: stripeBackground }}>{formatDateTime(detail.payoutDate, false)}</td>
                                                        <td className="pt-3 text-end  text-bold" style={{ backgroundColor: stripeBackground }}>{toLocalePriceNoDecimal(detail.grossAmount)}</td>
                                                        <td className="pt-3 text-end" style={{ backgroundColor: stripeBackground }}>{detail?.fee ? toLocalePriceNoDecimal(detail.fee) : "0"}</td>
                                                        <td className="pt-3 text-end" style={{ backgroundColor: stripeBackground }}>{detail?.tds ? toLocalePriceNoDecimal(detail.tds) : "0"}</td>
                                                        <td className="pt-3 text-end" style={{ backgroundColor: stripeBackground }}>{toLocalePriceNoDecimal(detail?.netAmount) ?? '0'} </td>
                                                    </tr>
                                                );
                                            })}

                                            <tr className="fs-normal-medium text-center" style={{ backgroundColor: stripeBackground, border: '1.5px solid #e0e0e0' }}>
                                                <td className="pt-3 fw-bold text-center" style={{ backgroundColor: stripeBackground }}>Total</td>
                                                <td className="pt-3 text-end fw-bold" style={{ backgroundColor: stripeBackground }}>{toLocalePriceNoDecimal(payout.totalGrossAmount)}</td>
                                                <td className="pt-3 text-end fw-bold" style={{ backgroundColor: stripeBackground }}>{toLocalePriceNoDecimal(payout.totalFee)}</td>
                                                <td className="pt-3 text-end fw-bold" style={{ backgroundColor: stripeBackground }}>{toLocalePriceNoDecimal(payout.totalTds)}</td>
                                                <td className="pt-3 text-end fw-bold" style={{ backgroundColor: stripeBackground }}>{toLocalePriceNoDecimal(payout.totalNetAmount)}</td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>

                    </div>
                ) : (
                    <p>No records</p>
                )
            }

        </DialogLayout>

    )
}

export default ViewPayoutSchedule;